import { WebAuthnRegisterApi } from "@/features/auth/api-client/webauthn-register-api-client";
import { SecurityApiClient } from "@/features/security/api-client/api-client.ts";
import {
  type UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery
} from "@tanstack/react-query";
import { ActivateF2aParams, Setup2faResponse } from "../types";

export const useSecurity = () => {
  const newWebauthn = (options?: UseMutationOptions<string | null | undefined, Error, string>) =>
    useMutation<string | null | undefined, Error, string>({
      mutationFn: async (deviceName: string) => {
        const challenge = await WebAuthnRegisterApi.registerWebAuthnUser(deviceName, false);
        return challenge;
      },
      ...options
    });
  const useSetup2fa = (options?: UseMutationOptions<Setup2faResponse, Error>) =>
    useMutation<Setup2faResponse, Error>({
      mutationFn: async () => {
        try {
          return await SecurityApiClient.setup2fa();
        } catch (_) {
          throw new Error("We were unable to setup 2FA. Please try again later.");
        }
      },
      ...options
    });
  const useReset2Fa = (options?: UseMutationOptions<Setup2faResponse, Error>) =>
    useMutation<Setup2faResponse, Error>({
      mutationFn: async () => {
        try {
          return await SecurityApiClient.reset2Fa();
        } catch (_) {
          throw new Error("Invalid reset2fa.");
        }
      },
      ...options
    });

  const useActivate2fa = (options?: UseMutationOptions<boolean, Error, ActivateF2aParams>) => {
    return useMutation<boolean, Error, ActivateF2aParams>({
      mutationFn: async ({ otp, secret }: ActivateF2aParams) => {
        try {
          return await SecurityApiClient.activate2fa(otp, secret);
        } catch (e) {
          console.error("Error activating 2FA:", e); // Log the original error
          throw new Error("Failed to activate 2FA. Please try again."); // More user-friendly error message
        }
      },
      ...options
    });
  };
  const getWebAuthnUserDevices = (email: string) =>
    useQuery({
      queryKey: ["security", "user-devices"],
      queryFn: async () => {
        try {
          return await WebAuthnRegisterApi.retrieveWebAuthnActiveCredentials();
        } catch (_) {
          return [];
        }
      },
      enabled: !!email
    });
  const getIs2faActive = () =>
    useQuery({
      queryKey: ["security", "is2faActive"],
      queryFn: async () => await SecurityApiClient.is2faActive(),
      enabled: true
    });
  const getIsWebAuthnActivated = (options?: UseQueryOptions<boolean, Error>) =>
    useQuery({
      queryKey: ["security", "isWebAuthnActivated"],
      queryFn: async () => (await WebAuthnRegisterApi.checkActivation())?.isWebAuthnActivated,
      ...options
    });
  return {
    newWebauthn,
    useSetup2fa,
    useReset2Fa,
    useActivate2fa,
    getWebAuthnUserDevices,
    getIs2faActive,
    getIsWebAuthnActivated
  };
};
